// @flow
import { of } from 'rxjs';
import { mergeMap, map, catchError } from 'rxjs/operators';
import update from 'immutability-helper';
import {
  endpoints,
  getFlightApi,
  saveDataApi,
} from './api';
import { setNewAlert } from '../alert/duck';
import { ofType } from 'redux-observable';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../error';
import type { Flight } from '../../entities';

type FlightLandingAction = {
  +type: string;
  +id?: string;
  +flight?: Flight;
  +fetchFlightError?: HTTPError;
  +saveDataError?: HTTPError;
};

export type FlightLandingState = {
  +isFetchingFlight?: boolean;
  +flight?: Flight;
  +fetchFlightError?: HTTPError;
  +isSavingData?: boolean;
  +saveDataError?: HTTPError;
};

// Actions
const FLIGHT_LANDING_FETCH_FLIGHT_REQUEST = 'FLIGHT_LANDING_FETCH_FLIGHT_REQUEST';
const FLIGHT_LANDING_FETCH_FLIGHT_SUCCESS = 'FLIGHT_LANDING_FETCH_FLIGHT_SUCCESS';
const FLIGHT_LANDING_FETCH_FLIGHT_ERROR = 'FLIGHT_LANDING_FETCH_FLIGHT_ERROR';

const FLIGHT_LANDING_SAVE_DATA_REQUEST = 'FLIGHT_LANDING_SAVE_DATA_REQUEST';
const FLIGHT_LANDING_SAVE_DATA_SUCCESS = 'FLIGHT_LANDING_SAVE_DATA_SUCCESS';
const FLIGHT_LANDING_SAVE_DATA_ERROR = 'FLIGHT_LANDING_SAVE_DATA_ERROR';

// Action Creators
export function fetchFlight(id: string) {
  return {
    type: FLIGHT_LANDING_FETCH_FLIGHT_REQUEST,
    id,
  };
}

export function fetchFlightSuccess(flight: Flight) {
  return {
    type: FLIGHT_LANDING_FETCH_FLIGHT_SUCCESS,
    flight,
  };
}

export function fetchFlightErrorFn(fetchFlightError: HTTPError): FlightLandingAction {
  return {
    type: FLIGHT_LANDING_FETCH_FLIGHT_ERROR,
    fetchFlightError,
  }
}

export function saveData(id: string, email: string, phone: string, product: string) {
  return {
    type: FLIGHT_LANDING_SAVE_DATA_REQUEST,
    id,
    email,
    phone,
    product,
  };
}

export function saveDataSuccess() {
  return {
    type: FLIGHT_LANDING_SAVE_DATA_SUCCESS,
  };
}

export function saveDataErrorFn(saveDataError: HTTPError): FlightLandingAction {
  return {
    type: FLIGHT_LANDING_SAVE_DATA_ERROR,
    saveDataError,
  }
}

export const actions = {
  FLIGHT_LANDING_FETCH_FLIGHT_REQUEST,
  FLIGHT_LANDING_FETCH_FLIGHT_SUCCESS,
  FLIGHT_LANDING_FETCH_FLIGHT_ERROR,
  FLIGHT_LANDING_SAVE_DATA_REQUEST,
  FLIGHT_LANDING_SAVE_DATA_SUCCESS,
  FLIGHT_LANDING_SAVE_DATA_ERROR,
  fetchFlight,
  fetchFlightSuccess,
  fetchFlightErrorFn,
  saveData,
  saveDataSuccess,
  saveDataErrorFn,
};

export const initialState: FlightLandingState = {};

// Reducer
export default function reducer(state: FlightLandingState = initialState,
                                action: FlightLandingAction): FlightLandingState {
  switch (action.type) {
    case FLIGHT_LANDING_FETCH_FLIGHT_REQUEST:
      return update(state, {
        isFetchingFlight: { $set: true }
      });
    case FLIGHT_LANDING_FETCH_FLIGHT_SUCCESS:
      return update(state, {
        $unset: ['isFetchingFlight'],
        flight: { $set: action.flight }
      });
    case FLIGHT_LANDING_FETCH_FLIGHT_ERROR:
      return update(state, {
        fetchFlightError: { $set: action.fetchFlightError }
      });
    case FLIGHT_LANDING_SAVE_DATA_REQUEST:
      return update(state, {
        isSavingData: { $set: true }
      });
    case FLIGHT_LANDING_SAVE_DATA_SUCCESS:
      return update(state, {
        $unset: ['isSavingData'],
      });
    case FLIGHT_LANDING_SAVE_DATA_ERROR:
      return update(state, {
        saveDataError: { $set: action.saveDataError },
        $unset: ['isSavingData'],
      });
    default:
      return state;
  }
}

// Epics
export function fetchFlightLandingFlightEpic(action$: Observable<FlightLandingAction>): Observable<FlightLandingAction> {
  return action$.pipe(
    ofType(FLIGHT_LANDING_FETCH_FLIGHT_REQUEST),
    mergeMap(({ id }) => getFlightApi(id, {
        route: endpoints.GET_FLIGHT.replace(':id', id),
      }).pipe(
      map((response: Flight) => fetchFlightSuccess(response)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchFlightErrorFn(e),
            setNewAlert('flight-landing', 'error', undefined, 'httpErrors.fetch.500'),
          );
        } else if (e.code === 400) {
          return of(fetchFlightErrorFn(e),
            setNewAlert('flight-landing', 'error', undefined, 'httpErrors.fetch.400'),
          );
        }
        return of(fetchFlightErrorFn(e))
      }))
    ));
}

export function saveDataLandingFlightEpic(action$: Observable<FlightLandingAction>): Observable<FlightLandingAction> {
  return action$.pipe(
    ofType(FLIGHT_LANDING_SAVE_DATA_REQUEST),
    mergeMap(({ id, email, phone, product }) => 
      saveDataApi({
        email,
        whatsappPhone: phone,
        request: product,
      }, {
        route: endpoints.SAVE_DATA.replace(':id', id),
      }).pipe(
        map(() => saveDataSuccess()),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(saveDataErrorFn(e),
              setNewAlert('flight-landing', 'error', undefined, 'httpErrors.fetch.500'),
            );
          } else if (Math.round(e.code / 100) === 4) {
            return of(saveDataErrorFn(e),
              setNewAlert('flight-landing', 'error', undefined, 'httpErrors.fetch.400'),
            );
          }
          return of(saveDataErrorFn(e));
        }))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/flight-landing/duck.js